import {
  actualizarPrograma,
  crearPrograma,
  eliminarPrograma,
  getProgramas,
} from 'api/programa'
import {
  CAMPOS_PROGRAMA,
  CARPETA_ACREDITACION_CALIDAD,
  CARPETA_REGISTRO_CALIFICADO,
} from 'components/ProgramaStepForm/constantes'
import { useMutation, useQuery, useQueryClient } from 'react-query'

import swal from 'sweetalert2'

// @hooks
import { useSubirArchivo } from 'utils/aws'

const key = 'programas'
export function useObtenerPrograma(buscar) {
  return useQuery(key, () => getProgramas(buscar))
}

export function useCrearPrograma() {
  const queryClient = useQueryClient()
  return useMutation(crearPrograma, {
    onSuccess: (response) => {
      const { data } = response
      queryClient.setQueryData(key, (prevState) => {
        const results = [data, ...(prevState?.results || [])]
        return { ...prevState, results }
      })
    },
  })
}

export function useActualizarPrograma() {
  const queryClient = useQueryClient()
  return useMutation(actualizarPrograma, {
    onSuccess: (_, value) => {
      const { id, values: programaActualizado } = value
      queryClient.setQueryData(key, (prevState) => {
        const results = prevState.results.map((programa) => {
          let nuevoPrograma = programa
          if (id === programa.id) {
            nuevoPrograma = programaActualizado
          }
          return nuevoPrograma
        })
        return { ...prevState, results }
      })
    },
  })
}

export function useEliminarPrograma() {
  const queryClient = useQueryClient()
  return useMutation(eliminarPrograma, {
    onSuccess: (_, value) => {
      queryClient.setQueryData(key, (prevState) => {
        const results = prevState.results.filter(
          (programa) => programa.id !== value.id,
        )
        return { ...prevState, results }
      })
    },
  })
}

export function usePrograma(onToggle = () => {}) {
  const queryClient = useQueryClient()
  const { mutateAsync: eliminarPrograma } = useEliminarPrograma()
  const { mutateAsync: actualizarPrograma } = useActualizarPrograma()
  const { mutateAsync: crearPrograma } = useCrearPrograma()
  const [subirArchivo] = useSubirArchivo()

  const handleCrearPrograma = async (
    { rc_archivo_url, ac_archivo_url, ...values },
    acciones,
  ) => {
    try {
      const { data, onSuccessMessage } = await crearPrograma(values)
      const promesas = [
        subirArchivo(
          rc_archivo_url,
          [CARPETA_REGISTRO_CALIFICADO, data.id].join('/'),
        ),
      ]
      data.rc_archivo_url = [
        CARPETA_REGISTRO_CALIFICADO,
        data.id,
        rc_archivo_url?.name,
      ].join('/')

      if (values[CAMPOS_PROGRAMA.PROGRAMA_ACREDITADO]) {
        promesas.push(
          subirArchivo(
            ac_archivo_url,
            [CARPETA_ACREDITACION_CALIDAD, data.id].join('/'),
          ),
        )
        data.ac_archivo_url = [
          CARPETA_REGISTRO_CALIFICADO,
          data.id,
          ac_archivo_url?.name,
        ].join('/')
      }
      const results = await Promise.all(promesas)
      const hasErrors = results.some(({ error }) => !!error)
      if (hasErrors) {
        const errors = results.map(({ error }) => error)
        // eslint-disable-next-line
        throw {
          [CAMPOS_PROGRAMA.RC_FILES]: [errors[0].message],
        }
      }

      // Actualizar programa creado con la url de los archivos
      await actualizarPrograma({
        id: data.id,
        values: data,
      })

      const message = `El programa ${data.nombre} fue creado satisfactoriamente!`
      onSuccessMessage({ text: message })
      acciones.resetForm(data)
      onToggle()
    } catch (error) {
      acciones.setErrors(error)
    }
  }

  const handleActualizarPrograma = async ({ id, values, acciones }) => {
    try {
      const { data, onSuccessMessage } = await actualizarPrograma({
        id,
        values,
      })
      if (data) {
        onSuccessMessage({
          text: 'El programa fue actualizado satisfactoriamente!',
        })
        onToggle()
      }
    } catch (error) {
      acciones.setErrors(error)
    }
  }

  const handleEliminarPrograma = (id) => {
    swal
      .fire({
        title: 'Estás seguro?',
        text: 'Recuerda que no podrás revertir estos cambios!',
        type: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Si, eliminalo!',
      })
      .then(async ({ value }) => {
        if (value) {
          const { data, onSuccessMessage } = await eliminarPrograma(id)
          const message = `El programa ${data} fue eliminado satisfactoriamente!`
          onSuccessMessage({ text: message })
        }
      })
  }

  const handleBuscarPrograma = async (value) => {
    queryClient.prefetchQuery(key, () => getProgramas(value))
  }

  return {
    obtenerProgramas: useObtenerPrograma,
    handleEliminarPrograma,
    handleActualizarPrograma,
    handleCrearPrograma,
    handleBuscarPrograma,
  }
}
